import {
    Button,
    makeStyles,
    mergeClasses,
    Popover,
    PopoverProps,
    PopoverSurface,
    PopoverTrigger,
    tokens,
} from '@fluentui/react-components';
import React, { ReactNode, useId, useState } from 'react';
import { customPalette } from '../../theme';

export interface IPopoverCalloutProps {
    /** Icon shown on the subtle Button trigger. */
    icon: ReactNode;
    /** Accessible name for the trigger button. */
    buttonAriaLabel: string;
    /** Optional visible label on the trigger (icon-only when omitted). */
    label?: string;
    /** Optional id for the trigger button. */
    buttonId?: string;
    children?: ReactNode;
    onDismiss?: () => void;
    /**
     * Optional dark callout surface (ported from Fluent 8 `getCalloutProps`):
     * neutralDarker background, themeLight text, matching arrow/beak fill.
     */
    darkSurface?: boolean;
    /** Show the positioning arrow (Fluent 8 beak). Defaults to true when `darkSurface` is set. */
    withArrow?: boolean;
    /** Positioning for the Popover surface. */
    positioning?: PopoverProps['positioning'];
    /** Extra class on PopoverSurface. */
    surfaceClassName?: string;
    /** Accessible name for the dialog surface. Defaults to `buttonAriaLabel`. */
    dialogAriaLabel?: string;
    /** When false, do not trap focus. Default true. */
    trapFocus?: boolean;
}

const useStyles = makeStyles({
    trigger: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    surface: {
        padding: tokens.spacingVerticalS,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow16,
        maxWidth: '320px',
    },
    darkSurface: {
        backgroundColor: customPalette.neutralDarker,
        color: customPalette.themeLight,
        borderRadius: '4px',
        padding: '5px 12px 7px 12px',
        lineHeight: '16px',
        // Fluent 9 arrow/beak fill (replaces Fluent 8 beak + beakCurtain).
        '::before': {
            backgroundColor: customPalette.neutralDarker,
        },
    },
});

/**
 * Shared Fluent 9 Popover callout host (replaces Fluent 8 Callout pattern).
 * Trigger is a Button with appearance="subtle" — not Fluent 8 Link.
 * Surface uses role="dialog" to match CalloutIcon's prior dialog role.
 */
const PopoverCallout: React.FC<IPopoverCalloutProps> = ({
    icon,
    buttonAriaLabel,
    label,
    buttonId,
    children,
    onDismiss,
    darkSurface = false,
    withArrow,
    positioning,
    surfaceClassName,
    dialogAriaLabel,
    trapFocus = true,
}) => {
    const styles = useStyles();
    const generatedId = useId();
    const triggerId = buttonId ?? generatedId;
    const [isOpen, setIsOpen] = useState(false);
    const showArrow = withArrow ?? darkSurface;

    const defaultPositioning: PopoverProps['positioning'] = {
        position: 'below',
        align: 'center',
        offset: { mainAxis: 10 },
        withArrow: showArrow,
    };

    return (
        <Popover
            open={isOpen}
            onOpenChange={(_ev, data) => {
                setIsOpen(data.open);
                if (!data.open) {
                    onDismiss?.();
                }
            }}
            positioning={positioning ?? defaultPositioning}
            trapFocus={trapFocus}
        >
            <PopoverTrigger disableButtonEnhancement>
                <Button
                    id={triggerId}
                    icon={icon}
                    aria-label={buttonAriaLabel}
                    aria-expanded={isOpen}
                    aria-haspopup='dialog'
                    appearance='subtle'
                    className={styles.trigger}
                >
                    {label}
                </Button>
            </PopoverTrigger>
            <PopoverSurface
                className={mergeClasses(styles.surface, darkSurface && styles.darkSurface, surfaceClassName)}
                role='dialog'
                aria-label={dialogAriaLabel ?? buttonAriaLabel}
            >
                {children}
            </PopoverSurface>
        </Popover>
    );
};

export default PopoverCallout;
